import {
    PERIOD_UNITS,
    YEAR_STEPS,
    currentPeriodKey,
    parsePeriodKey,
    previousPeriodKey,
} from './expense-report-period'
import type { PeriodUnit, YearStep } from './expense-report-period'
import { todayYMD } from '@/lib/date-presets'

/**
 * Bộ lọc Báo cáo chi phí theo danh mục, nằm trên URL (gửi link là mở đúng).
 * Kỳ để trống = kỳ hiện tại; so sánh để trống = kỳ liền trước của kỳ xem.
 */
export interface ExpenseReportSearch {
    display: 'chart' | 'report'
    unit: PeriodUnit
    period?: string
    compare?: string
    /** Mốc biểu đồ khi xem theo Năm; trống = Quý. */
    step?: YearStep
    /** id danh mục đang lọc; trống = tất cả. */
    category?: string[]
}

export const DEFAULT_UNIT: PeriodUnit = 'month'
const DEFAULT_STEP: YearStep = 'quarter'

/** Khoá kỳ đúng loại và không ở tương lai. */
function pickPeriod(raw: unknown, unit: PeriodUnit, today: string) {
    if (typeof raw !== 'string') return undefined
    const p = parsePeriodKey(raw)
    if (!p || p.unit !== unit) return undefined
    // Khoá cùng loại so sánh được theo thứ tự chuỗi (YYYY, YYYY-MM, YYYY-Qn).
    return raw <= currentPeriodKey(unit, today) ? raw : undefined
}

function pickCategories(raw: unknown): string[] | undefined {
    const parts = Array.isArray(raw)
        ? raw.filter((v): v is string => typeof v === 'string')
        : typeof raw === 'string'
          ? raw.split(',')
          : []
    const ids = parts.map((s) => s.trim()).filter(Boolean)
    return ids.length > 0 ? ids : undefined
}

export function validateExpenseReportSearch(
    raw: Record<string, unknown>,
    today: string = todayYMD(),
): ExpenseReportSearch {
    const unit = PERIOD_UNITS.find((u) => u === raw.unit) ?? DEFAULT_UNIT
    return {
        display: raw.display === 'chart' ? 'chart' : 'report',
        unit,
        period: pickPeriod(raw.period, unit, today),
        compare: pickPeriod(raw.compare, unit, today),
        step: YEAR_STEPS.find((s) => s === raw.step),
        category: pickCategories(raw.category),
    }
}

export interface ResolvedExpenseSearch {
    viewKey: string
    compareKey: string
    step: YearStep
}

export function resolveExpenseSearch(
    search: ExpenseReportSearch,
    today: string,
): ResolvedExpenseSearch {
    const viewKey = search.period ?? currentPeriodKey(search.unit, today)
    return {
        viewKey,
        compareKey: search.compare ?? previousPeriodKey(viewKey),
        step: search.step ?? DEFAULT_STEP,
    }
}
